import React, { useState } from 'react';
import { ListPriceBody } from './ListPriceBody';
import { useClickOutside } from '../../../hooks/useClickOutside';
import { formatShort } from '../../../utils/formatters';
import { useFilterController } from '../../../../application/useFilterController';
import { useDebouncedRange } from '../../../hooks/useDebouncedRange';

export const ListPriceDropdown = () => {
  const { priceRange, setPriceRange } = useFilterController();

  const [ open, setOpen ] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setOpen(false));

  // Local <-> store sync with debounce
  const {
    minLocal,
    maxLocal,
    setMinLocal,
    setMaxLocal,
  } = useDebouncedRange(priceRange, setPriceRange, 600);

  const handleInputChange = (val: string, type: 'min' | 'max') => {
    const numeric = Number(val.replace(/[^0-9]/g, ''));
    if (type === 'min') setMinLocal(val ? numeric : undefined);
    if (type === 'max') setMaxLocal(val ? numeric : undefined);
  };

  const handleReset = () => {
    // Clear store range
    setPriceRange(null, null);
    // Local values will be reset by useDebouncedRange when filter.price updates,
    // but you can eagerly clear them too if you like:
    setMinLocal(undefined);
    setMaxLocal(undefined);
  };

  const displayLabel =
      priceRange?.min != null || priceRange?.max != null
        ? `${formatShort(priceRange?.min) || 'Any'} - ${
          formatShort(priceRange?.max) || 'Any'
        }`
        : 'Price';

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <button
        onClick={() => setOpen(prev => !prev)}
        className="border rounded px-3 py-2 bg-white text-gray-800 flex items-center justify-between min-w-[120px]"
      >
        <span className="text-sm">{displayLabel}</span>
        <svg
          className="ml-2 h-4 w-4 text-gray-500"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.293l3.71-4.06a.75.75 0 111.08 1.04l-4.25 4.65a.75.75 0 01-1.08 0l-4.25-4.65a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {open && (
        <div className="absolute left-0 mt-2 w-80 bg-white border rounded shadow-lg z-10 p-4">
          <ListPriceBody
            minLocal={minLocal}
            maxLocal={maxLocal}
            onMinChange={val => handleInputChange(val, 'min')}
            onMaxChange={val => handleInputChange(val, 'max')}
          />

          <div className="flex justify-between mt-3">
            <button
              onClick={handleReset}
              className="text-blue-600 text-sm font-medium hover:underline"
            >
                Reset
            </button>
            {/* No Apply button — debounce commits to store */}
          </div>
        </div>
      )}
    </div>
  );
};
